import './process-error-stats.css'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Input, PageHeader, Spinner, Text } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language]

const getCurrentDate = () => {
  const currentDate = new Date()
  currentDate.setHours(0)
  currentDate.setMinutes(0)
  currentDate.setSeconds(0)
  currentDate.setMilliseconds(0)
  return currentDate
}

/**
 * Conta le occorrenze di errori per unit.
 * @param {Array} jsonArray - Lista step in errore
 * @returns {Array<{unit: string, count: number}>} Conteggi per unit
 */
function countUnits(jsonArray) {
  const unitCountMap = {}

  jsonArray.forEach((item) => {
    const unit = item.unit
    if (!unitCountMap[unit]) {
      unitCountMap[unit] = 1
    } else {
      unitCountMap[unit]++
    }
  })

  return Object.keys(unitCountMap).map((unit) => ({
    unit,
    count: unitCountMap[unit]
  }))
}

const formatDateTime = (date) => {
  if (!date) return '-'
  const d = new Date(date)
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Pagina statistiche errori processi con filtro intervallo date.
 * @returns {JSX.Element} Componente error stats
 */
export function ProcessesErrorStats() {
  const [selectedStartDay, setSelectedStartDay] = useState(getCurrentDate())
  const [selectedFinalDay, setSelectedFinalDay] = useState(getCurrentDate())
  const { data: errorProcessesSteps = [], isSuccess } = useCQuery([
    'errorProcessStepsRange',
    selectedStartDay,
    selectedFinalDay
  ])
  const navigate = useNavigate()

  const errors = useMemo(
    () => errorProcessesSteps.filter((item) => item.stepState === 5),
    [errorProcessesSteps]
  )

  const countedErrors = useMemo(() => {
    const counted = countUnits(errors)
    counted.sort((a, b) => b.count - a.count)
    return counted
  }, [errors])

  const errorTableData = useMemo(
    () =>
      errors.map((e) => ({
        ...e,
        createdAtFormatted: formatDateTime(e.createdAt)
      })),
    [errors]
  )

  if (!isSuccess) return <Spinner />

  const maxCount = countedErrors[0]?.count || 1

  return (
    <div className="process-error-stats-page">
      <PageHeader
        title={t.processErrors}
        subtitle={t.statisticheErroriPerIntervallo}
        actions={
          <button type="button" className="nbtn" onClick={() => navigate('/process')}>
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
        }
      />

      <div className="ncard" style={{ marginBottom: 14 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-calendar" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.intervalloDate}</h3>
          </div>
        </div>
        <div className="ncard__body">
          <div className="process-error-stats-date-row">
            <div className="process-error-stats-date-group">
              <button
                type="button"
                className="nbtn nbtn--icon nbtn--sm"
                onClick={() =>
                  setSelectedStartDay(
                    new Date(selectedStartDay.setDate(selectedStartDay.getDate() - 1))
                  )
                }
              >
                <i className="pi pi-chevron-left" />
              </button>
              <div className="nfield nora-date-field" style={{ minWidth: 220 }}>
                <label>{t.dataInizio}</label>
                <Input
                  date
                  showIcon
                  value={selectedStartDay}
                  id="selectedStartDay"
                  style={{ width: '100%' }}
                  onChange={({ selectedStartDay }) => setSelectedStartDay(selectedStartDay)}
                />
              </div>
              <button
                type="button"
                className="nbtn nbtn--icon nbtn--sm"
                onClick={() =>
                  setSelectedStartDay(
                    new Date(selectedStartDay.setDate(selectedStartDay.getDate() + 1))
                  )
                }
              >
                <i className="pi pi-chevron-right" />
              </button>
            </div>
            <div className="process-error-stats-date-group">
              <button
                type="button"
                className="nbtn nbtn--icon nbtn--sm"
                onClick={() =>
                  setSelectedFinalDay(
                    new Date(selectedFinalDay.setDate(selectedFinalDay.getDate() - 1))
                  )
                }
              >
                <i className="pi pi-chevron-left" />
              </button>
              <div className="nfield nora-date-field" style={{ minWidth: 220 }}>
                <label>{t.dataFine}</label>
                <Input
                  date
                  showIcon
                  value={selectedFinalDay}
                  id="selectedFinalDay"
                  style={{ width: '100%' }}
                  onChange={({ selectedFinalDay }) => setSelectedFinalDay(selectedFinalDay)}
                />
              </div>
              <button
                type="button"
                className="nbtn nbtn--icon nbtn--sm"
                onClick={() =>
                  setSelectedFinalDay(
                    new Date(selectedFinalDay.setDate(selectedFinalDay.getDate() + 1))
                  )
                }
              >
                <i className="pi pi-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ncard" style={{ padding: 0 }}>
        <div className="ncard__head" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ncard__ico">
            <i className="pi pi-list" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.dettaglioProcessiErrore}</h3>
          </div>
          <div className="ncard__head-actions">
            <span className="nbadge nbadge--err">
              {errors.length} {t.errori?.toLowerCase()}
            </span>
          </div>
        </div>
        {errors.length ? (
          <DataTable
            value={errorTableData}
            size="small"
            scrollable
            scrollHeight="500px"
            sortMode="single"
            sortField="createdAt"
            sortOrder={-1}
            removableSort
            paginator
            rows={25}
            className="ntable"
            emptyMessage={t.nonCiSonoErrori}
            onRowClick={(e) => navigate(`/process/stats/${e.data.processId}`)}
            rowHover
          >
            <Column
              field="processId"
              header={t.processo}
              sortable
              style={{ width: 90 }}
              body={(row) => (
                <Text
                  value={`#${row.processId}`}
                  size={13}
                  bold
                  style={{ color: 'var(--primary)', cursor: 'pointer' }}
                />
              )}
            />
            <Column
              field="unit"
              header={t.unit}
              sortable
              filter
              filterPlaceholder={t.cerca}
              style={{ minWidth: 180 }}
              body={(row) => (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{row.unit}</span>
              )}
            />
            <Column
              field="createdAt"
              header={t.data}
              sortable
              style={{ width: 140 }}
              body={(row) => (
                <Text
                  value={row.createdAtFormatted}
                  size={13}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              )}
            />
            <Column
              field="executionTime"
              header={t.tempo}
              sortable
              style={{ width: 100 }}
              body={(row) => (
                <Text
                  value={
                    row.executionTime != null ? `${(row.executionTime / 1000).toFixed(1)}s` : '-'
                  }
                  size={13}
                />
              )}
            />
          </DataTable>
        ) : (
          <div className="ncard__body">
            <p style={{ margin: 0, color: 'var(--ink-500)' }}>{t.nonCiSonoErrori}</p>
          </div>
        )}
        <style>{`
          .process-error-stats-page .p-datatable-tbody > tr { cursor: pointer; }
          .process-error-stats-page .p-datatable-tbody > tr:hover > td { background: var(--surface-2); }
        `}</style>
      </div>

      <div className="ncard" style={{ marginTop: 14 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.unitInErrore}</h3>
          </div>
          <div className="ncard__head-actions">
            <span className="nbadge nbadge--err">
              {countedErrors.length} {t.unit}
            </span>
          </div>
        </div>
        <div className="ncard__body">
          {countedErrors.length ? (
            <div className="process-error-stats-bars">
              {countedErrors.map((ps) => (
                <div key={ps.unit} className="process-error-stats-bar">
                  <div className="process-error-stats-bar__head">
                    <span className="process-error-stats-bar__name">{ps.unit}</span>
                    <span className="ntable__num">{ps.count}</span>
                  </div>
                  <div className="process-error-stats-bar__track">
                    <div
                      className="process-error-stats-bar__fill"
                      style={{ width: `${Math.max(4, (ps.count / maxCount) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, color: 'var(--ink-500)' }}>{t.nonCiSonoErrori}</p>
          )}
        </div>
      </div>
    </div>
  )
}
